// react
import * as React from 'react';
import { useDispatch } from 'react-redux';

import PropTypes from 'prop-types';

import { useSnackbar } from 'notistack';

import { Form, FormikProvider, useFormik } from 'formik';

// IconButton
import { Icon } from '@iconify/react';
import CloseIcon from '@iconify/icons-mdi/close-circle-outline';
import commentOutline from '@iconify/icons-mdi/comment-outline';
import commentText from '@iconify/icons-mdi/comment-text';
import saveIcon from '@iconify/icons-eva/save-outline';

// material
import { styled } from '@mui/material/styles';
import { LoadingButton } from '@mui/lab';
import {
  ListItemIcon,
  ListItemText,
  MenuItem,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton
} from '@mui/material';

// local
import { editItem } from '../../fetch';
import { reportTripsActions } from '../../store';
import updateFlightList from './updateFlightList';
import { useTranslation } from '../../common/LocalizationProvider';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2)
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1)
  }
}));

function BootstrapDialogTitle(props) {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500]
          }}
        >
          <Icon icon={CloseIcon} />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
}

BootstrapDialogTitle.propTypes = {
  children: PropTypes.node,
  onClose: PropTypes.func.isRequired
};

FlightListAddComment.propTypes = {
  reportTrip: PropTypes.object.isRequired,
  disabled: PropTypes.bool.isRequired
};

export default function FlightListAddComment({ reportTrip, disabled }) {
  const t = useTranslation();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const [isOpen, setIsOpen] = React.useState(false);

  const formik = useFormik({
    initialValues: {
      id: reportTrip.affectationVolsId,
      deviceId: reportTrip.deviceId,
      piloteId: reportTrip.driverUniqueId,
      instructeurId: reportTrip.instructeurUniqueId,
      startTime: reportTrip.startTime,
      endTime: reportTrip.endTime,
      groupId: reportTrip.groupId,
      attributes: {},
      comment: reportTrip.commentAffectationVols,
      flag: reportTrip.flagAffectationVols,
      favorite: reportTrip.favoriteAffectationVols,
      remarquableTrace: reportTrip.pointRemarquableTrace > 0
    },
    onSubmit: async (values) => {
      const response = await editItem('affectationVols', values.id, values);
      if (response.ok) {
        enqueueSnackbar(t('FL_Menu_Comment_Success'), {
          variant: 'success'
        });
        dispatch(reportTripsActions.update(await updateFlightList(values)));
      } else {
        const error = await response.text();
        const message = error.slice(0, error.indexOf('-'));
        enqueueSnackbar(message, { variant: 'error' });
      }

      setSubmitting(false);
      setIsOpen(false);
    }
  });
  const { handleSubmit, isSubmitting, getFieldProps, setSubmitting } = formik;

  return (
    <>
      <MenuItem
        onClick={() => setIsOpen(true)}
        sx={{ color: 'text.secondary' }}
        disabled={disabled}
      >
        <ListItemIcon>
          <Icon
            icon={
              reportTrip.commentAffectationVols !== null &&
              reportTrip.commentAffectationVols !== '' &&
              reportTrip.commentAffectationVols !== ' ' &&
              reportTrip.commentAffectationVols !== '0'
                ? commentText
                : commentOutline
            }
            width={24}
            height={24}
          />
        </ListItemIcon>
        <ListItemText
          primary={t('FL_Menu_Comment')}
          primaryTypographyProps={{ variant: 'body2' }}
        />
      </MenuItem>

      <BootstrapDialog onClose={() => setIsOpen(false)} open={isOpen}>
        <FormikProvider value={formik}>
          <BootstrapDialogTitle onClose={() => setIsOpen(false)}>
            {t('FL_Menu_Comment')}
          </BootstrapDialogTitle>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <DialogContent dividers>
              <TextField
                autoFocus
                fullWidth
                sx={{ width: '350px' }}
                multiline
                rows={4}
                variant="standard"
                {...getFieldProps('comment')}
                value={getFieldProps('comment').value || ''}
              />
            </DialogContent>
            <DialogActions>
              <LoadingButton
                fullWidth
                size="large"
                type="submit"
                variant="contained"
                loading={isSubmitting}
                startIcon={<Icon icon={saveIcon} />}
              >
                {t('btn_Save')}
              </LoadingButton>
            </DialogActions>
          </Form>
        </FormikProvider>
      </BootstrapDialog>
    </>
  );
}
